import { Box, Button, Card, CardContent, FormControl, InputLabel, MenuItem, Select, Switch } from "@mui/material";
import { CellKey, LensChronologyConfigView } from "generated/graphql";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import produce from "immer";
import { FC, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAppSelector } from "redux/hooks";
import {
  createChronologyConfig,
  deleteChronologyConfig,
  fetchChronologyConfig,
  selectChronologyConfig,
  selectProfileBuilderFields,
  updateChronologyConfig,
} from "redux/slices/lensEditor";
import { P } from "theme/typography";

const newLensChronologyConfig = (lensId: number): LensChronologyConfigView => ({
  id: -1,
  lensId,
  config: {
    fields: [],
  },
  updatedAt: Date.now().toString(),
});

const ChronologyExportBuilder: FC = () => {
  const lensIdParams = useParams<{ lensId: string }>().lensId;
  const lensId = parseInt(lensIdParams || "");
  const formFields = useAppSelector(selectProfileBuilderFields);
  const chronologyConfig = useAppSelector(selectChronologyConfig);
  const [config, setConfig] = useState<LensChronologyConfigView>(newLensChronologyConfig(lensId));
  const snackpatch = useAppDispatchWithSnackbar();

  const toggleField = (fieldId: number, enabled: boolean) => {
    setConfig(
      produce((draft) => {
        if (draft !== undefined) {
          const foundIndex = draft.config.fields.findIndex((f) => f.fieldId === fieldId);
          if (enabled) {
            draft.config.fields.push({
              fieldId,
            });
          } else if (foundIndex > -1) {
            draft.config.fields.splice(foundIndex, 1);
          }
        }
      })
    );
  };

  const setEventCell = (fieldId: number, cellId: number | undefined) => {
    setConfig(
      produce((draft) => {
        if (draft !== undefined) {
          const foundConfig = draft.config.fields.find((f) => f.fieldId === fieldId);

          if (cellId !== undefined && foundConfig !== undefined) {
            foundConfig.cellId = cellId;
          } else {
            delete foundConfig?.cellId;
          }
        }
      })
    );
  };

  const saveConfig = () => {
    if (config.id !== -1) {
      snackpatch(updateChronologyConfig({ lensId, config: config.config, configId: config.id }), {
        successMessage: "Lens Chronology Updated",
        errorMessage: "Unable to update Lens Chronology",
      });
    } else {
      snackpatch(createChronologyConfig({ lensId, config: config.config }), {
        successMessage: "Lens Chronology Created",
        errorMessage: "Unable to create Lens Chronology",
      });
    }
  };

  const deleteConfig = () => {
    if (config.id !== -1) {
      snackpatch(deleteChronologyConfig({ configId: config.id }), {
        successMessage: "Lens Chronology Deleted",
        errorMessage: "Unable to delete Lens Chronology",
      }).then(() => {
        setConfig(newLensChronologyConfig(lensId));
      });
    }
  };

  const resetConfig = () => {
    if (chronologyConfig !== undefined) {
      setConfig(chronologyConfig);
    }
  };

  useEffect(() => {
    snackpatch(fetchChronologyConfig({ lensId }), {
      errorMessage: "Failed to fetch chronology config",
    });
  }, [lensId]);

  useEffect(() => {
    if (chronologyConfig !== undefined) {
      setConfig(chronologyConfig);
    }
  }, [chronologyConfig]);

  return (
    <Box sx={{ p: 2 }}>
      {formFields.map((formField) => {
        const exportConfig = config?.config.fields.find((f) => f.fieldId === formField.id);
        const dateCell = formField.cells.find((c) => c.key === CellKey.PrimaryDate);
        const providerCell = formField.cells.find((c) => c.key === CellKey.Provider);
        return (
          <Card key={formField.id} sx={{ mb: 2 }} variant="outlined">
            <CardContent sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <P variant="subtitle3">
                  {formField.outlinePrefix} {formField.name}
                </P>
                <Switch
                  checked={exportConfig !== undefined}
                  onChange={(event) => toggleField(formField.id, event.target.checked)}
                />
              </Box>

              {dateCell !== undefined && (
                <Box sx={{ display: "flex" }}>
                  <P variant="body1">Date Cell:</P>
                  <P variant="body4" sx={{ color: "grey.400", ml: 1 }}>
                    {dateCell.name}
                  </P>
                </Box>
              )}
              {providerCell !== undefined && (
                <Box sx={{ display: "flex" }}>
                  <P variant="body1">Provider Cell:</P>
                  <P variant="body4" sx={{ color: "grey.400", ml: 1 }}>
                    {providerCell.name}
                  </P>
                </Box>
              )}

              {exportConfig !== undefined && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Event Cell</InputLabel>
                  <Select
                    value={exportConfig.cellId}
                    label="Event Cell"
                    onChange={(event) => {
                      setEventCell(formField.id, event.target.value ? parseInt(`${event.target.value}`) : undefined);
                    }}>
                    {formField.cells.map((cell) => (
                      <MenuItem key={cell.id} value={cell.id}>
                        {cell.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </CardContent>
          </Card>
        );
      })}

      <Box sx={{ display: "flex", mt: 1 }}>
        <Button variant="outlined" color="primary" onClick={deleteConfig}>
          Delete
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" color="primary" sx={{ mr: 2 }} onClick={resetConfig}>
          Undo
        </Button>
        <Button variant="contained" color="primary" onClick={saveConfig}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default ChronologyExportBuilder;
